<template>
    <v-row id="sidebar" class="sidebar"
           :class="[
            overlay ? 'sidebar-overlay' : '',
            overlay & panel === 0 ? 'sidebar-overlay-active' : ''
        ]"
           align-content="start">

        <v-col cols="12">
            <strong>{{ has(pagination, 'totalWithExtraItems') ? pagination.totalWithExtraItems : pagination.total }} results found</strong>
        </v-col>
        <v-col cols="10" lg="12" class="mr-md-1">
            <vuetify-google-autocomplete
                class="autocomplete-field"
                id="gmap-autocomplete"
                v-model="filters.location"
                prepend-inner-icon="mdi-map-marker"
                placeholder="Location"
                solo
                clearable
                country="uk"
                types="geocode"
                flat
                background-color="#ffffff"
                :selection-required="true"
                height="52px"
                outlined
                v-on:placechanged="locationChanged"
                prepend-icon-inner="mdi-map-marker"
            >
            </vuetify-google-autocomplete>
        </v-col>

        <v-col cols="2" v-if="isMobile">
            <v-btn
                class="v-btn-sort px-2"
                color="light"
                tile
                block
                :elevation="0"
                @click="togglePanel()">
                <v-icon>mdi-filter-outline</v-icon>
            </v-btn>
        </v-col>

        <v-expansion-panels flat tile accordion v-model="panel">
            <v-expansion-panel>
                <v-expansion-panel-content>
                    <v-row class="mx-0">
                        <!-- <v-col v-if="overlay">
                            <a @click="togglePanel()">
                                <strong class="grey--text text--darken-4">Less filters</strong>
                                <font-awesome-icon icon="fa-solid fa-xmark" class="pl-1"/>
                            </a>
                        </v-col> -->

                        <v-col cols="12" sm="8" v-if="!map.visible && isMobile">
                            <v-select
                                v-model="filters.sort_by"
                                label="Sort by"
                                :items="sortOptions"
                                outlined
                                class="no-border-radius z-index-15"
                                background-color="white"
                                @change="filterChanged()"
                            ></v-select>
                        </v-col>

                        <v-col
                            cols="12"
                            sm="8"
                            md="12"
                            class="mr-md-1"
                            v-for="(filter,index) in filterOptions.filters" :key="has(filter,'id') ? filter.id : index"
                            v-if="showFilter(filter)"
                        >
                            <div class="p-1 mb-6 search-facets" v-if="getFacet(filter.value)">
                                <div class="mb-3 border-bottom font-weight-semi-bold">
                                    {{ filter.text }}
                                </div>
                                <div :id="'facet-container-' + filter.value" class="facet-container"
                                    :class="'facet-container-' + filter.value">
                                    <div :class="'multi-value-facet-' + filter.value"
                                        class="multi-value-facet" v-if="getFacet(filter.value).multiValue">
                                        <v-checkbox
                                            :key="index"
                                            v-for="(option, index) in getFacet(filter.value).options"
                                            :value="option.value"
                                            :disabled="option.count == 0"
                                            v-model="filters.facets[filter.value]"
                                            @change="filterChanged()"
                                            hide-details
                                        >
                                            <template v-slot:label>
                                                <v-row>
                                                    <v-col cols="9">{{ option.text }}</v-col>
                                                    <v-col class="text-right" cols="3">
                                                        <v-chip small>{{ option.count }}</v-chip>
                                                    </v-col>
                                                </v-row>
                                            </template>
                                        </v-checkbox>
                                    </div>
                                    <div :class="'not-multi-value-facet-' + filter.value"
                                        class="not-multi-value-facet" v-if="!getFacet(filter.value).multiValue">
                                        <v-select
                                            v-model="filters.facets[filter.value]"
                                            label="Select"
                                            :items="getFacet(filter.value).options"
                                            outlined
                                            @change="filterChanged()"
                                            clearable
                                            background-color="white"
                                            class="no-border-radius"
                                        >
                                            <template slot="item" slot-scope="data">
                                                <v-row>
                                                    <v-col cols="9">{{ data.item.text }}</v-col>
                                                    <v-col class="text-right" cols="3">
                                                        <v-chip small>{{ data.item.count }}</v-chip>
                                                    </v-col>
                                                </v-row>
                                            </template>
                                        </v-select>
                                    </div>
                                </div>
                            </div>
                            <div class="p-1 mb-6 search-filters" v-else-if="filter.value == 'occupancyType'">
                                <v-select
                                    v-model="filters.occupancy_type"
                                    :label="filter.text"
                                    :items="occupancyChoices"
                                    outlined
                                    @input="setFilter('occupancy_type', filters.occupancy_type, false)"
                                    background-color="white"
                                    class="no-border-radius"
                                >
                                </v-select>
                            </div>
                            <div class="p-1 mb-6 search-filters" v-else-if="filter.value == 'radius'">
                                <v-select
                                    v-model="filters.radius"
                                    :label="filter.text"
                                    :items="radiusChoices"
                                    outlined
                                    @input="setFilter('radius', filters.radius, false)"
                                    background-color="white"
                                    class="no-border-radius"
                                >
                                </v-select>
                            </div>
                        </v-col>
                    </v-row>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-row>
</template>

<script>
import {
    defineComponent,
    ref,
    watch,
    onMounted,
    computed
} from "@vue/composition-api";
import updatePath from '../../composables/updatePath';
import {find, filter, forEach, includes, isArray, isNil, toNumber, map, sortBy, has, clone, findIndex} from "lodash";

export default defineComponent({
    name: 'search-filters',
    props: {
        isMobile: {
            type: Boolean,
            required: true
        },
        panel: {
            required: true
        },
        overlay: {
            type: Boolean,
            required: false
        },
        developmentFacets: {
            required: false
        },
        pagination: {
            required: true
        },
        searchFilters: {
            type: Object,
            required: true
        },
        sortOptions: {
            required: true
        },
        map: {
            type: Object,
            required: true
        },
        rectBounds: {
            type: Object,
            required: false
        },
        center: {
            type: Object,
            required: false
        },
        filterOptions: {
            type: Object
        }
    },
    setup(props, context) {
        const facets = ref([]);
        const reRenderFacets = ref(0);
        const filters = ref(props.searchFilters);
        const mainFilters = ref({
            location: "United Kingdom",
            radius: 3,
            occupancy_type: "for-sale",
            sort_by: props.filterOptions.sortBy
        });
        const max_full_value = ref(null);
        const occupancyChoices = ref([
            {
                text: "For sale",
                value: "for-sale"
            },
            {
                text: "To rent",
                value: "to-rent"
            }
        ]);
        const maxPriceChoices = ref([]);
        const radiusChoices = ref([
            {
                text: "+3 miles",
                value: 3
            },
            {
                text: "+5 miles",
                value: 5
            },
            {
                text: "+10 miles",
                value: 10
            },
            {
                text: "+15 miles",
                value: 15
            },
            {
                text: "+25 miles",
                value: 25
            },
            {
                text: "+40 miles",
                value: 40
            },
            {
                text: "+50 miles",
                value: 50
            }
        ]);

        const filterParams = ["max_full_value", "bedrooms", "max_monthly_rent"];
        const filteredFacets = computed(() => {
            const filtered = filter(facets.value, function (o) {
                if (!includes(filterParams, o.param)) {
                    return o;
                }
            });
            return filtered;
        });

        onMounted(() => {
            initFiltersPromise().then(
                function () {
                    context.emit('updateFilters', filters.value);
                    // context.emit('filtersReady');

                    window.onpopstate = function(event) {
                        context.root.$router.go(context.root.$router.history.current.fullPath)
                    }
                }, //success
                function (error) {
                    console.log(error);
                } //error
            );
        });

        watch(() => props.developmentFacets, (currentValue, oldValue) => {
            facets.value = currentValue;

            buildFacetFiltersPromise().then(function (result) {
                if (result) {
                    updatePath(context.root.$router, filters, false, props.rectBounds, props.center);
                    context.emit('filtersReady');
                }
                reRenderFacets.value += 1;
            });
        });

        watch(() => props.searchFilters.radius, (currentValue, oldValue) => {
            filterChanged(true);
        }, {deep: true});

        function togglePanel() {
            context.emit('togglePanel');
        }

        function initFiltersPromise() {
            let pathClone = clone(context.root.$route.query);

            return new Promise(function (resolve, reject) {
                if (context.root.$route.query.location) {
                    filters.value.location = context.root.$route.query.location;
                } else {
                    filters.value.location = mainFilters.value.location;
                    Object.assign(
                        pathClone,
                        {location: mainFilters.value.location}
                    )
                }

                if (context.root.$route.query.occupancy_type) {
                    filters.value.occupancy_type =
                        context.root.$route.query.occupancy_type;
                } else {
                    filters.value.occupancy_type = mainFilters.value.occupancy_type;
                    Object.assign(
                        pathClone,
                {occupancy_type: mainFilters.value.occupancy_type}
                    )
                }

                if (context.root.$route.query.radius) {
                    filters.value.radius = parseInt(context.root.$route.query.radius);
                } else {
                    filters.value.radius = mainFilters.value.radius;
                    Object.assign(
                        pathClone,
                        {radius: mainFilters.value.radius}
                    )
                }

                if (context.root.$route.query.page) {
                    filters.value.page = parseInt(context.root.$route.query.page);
                } else {
                    Object.assign(
                        pathClone,
                        {page: 1}
                    )
                    filters.value.page = 1;
                }

                if (context.root.$route.query.sort_by) {
                    filters.value.sort_by = context.root.$route.query.sort_by;
                } else {
                    Object.assign(
                        pathClone,
                        {sort_by: mainFilters.value.sort_by}
                    )
                    filters.value.page = 1;
                    filters.value.sort_by = mainFilters.value.sort_by;
                }

                // Facets
                for (var propName in context.root.$route.query) {
                    switch (propName) {
                        case "location":
                        case "occupancy_type":
                        case "radius":
                        case "page":
                        case "sort_by":
                            continue;
                            break;
                        case "max_deposit":
                        case "max_full_value":
                        case "max_monthly_rent":
                            filters.value.facets[propName] = toNumber(
                                context.root.$route.query[propName]
                            );
                            break;
                        case "schemes":
                        case "status":
                        case "types":
                        case "bedrooms":
                        case "bathrooms":
                        case "new_homes" :
                            if (isArray(context.root.$route.query[propName])) {
                                let result = map(
                                    context.root.$route.query[propName],
                                    function (v) {
                                        return toNumber(v);
                                    }
                                );
                                filters.value.facets[propName] = result;
                            } else {
                                filters.value.facets[propName] = [
                                    toNumber(context.root.$route.query[propName])
                                ];
                            }
                            break;
                    }
                }

                context.root.$router.replace({
                    query: pathClone
                });

                resolve();
            });
        }

        function buildFacetFiltersPromise() {
            return new Promise(function (resolve, reject) {
                let callAgain = false;
                if (facets.value && facets.value.length) {
                    for (var i = 0; i < facets.value.length; i++) {
                        switch (facets.value[i].multiValue) {
                            case true:
                                if (isNil(filters.value.facets[facets.value[i].param])) {
                                    filters.value.facets[facets.value[i].param] = [];
                                } else if (facets.value[i].param !== 'bedrooms') {
                                    if (filters.value.facets[facets.value[i].param].length > 0) {
                                        forEach(
                                            filters.value.facets[facets.value[i].param],
                                            function (value, key) {
                                                if (
                                                    facets.value[i].options &&
                                                    facets.value[i].options.length > 0
                                                ) {
                                                    let findings = find(facets.value[i].options, function (
                                                        o
                                                    ) {
                                                        return o.value == value;
                                                    });
                                                    if (isNil(findings)) {
                                                        filters.value.facets[facets.value[i].param].splice(
                                                            key,
                                                            1
                                                        );
                                                        callAgain = true;
                                                    }
                                                }
                                            }
                                        );
                                    }
                                }
                                break;
                            case false:
                                if (isNil(filters.value.facets[facets.value[i].param])) {
                                    filters.value.facets[facets.value[i].param] = "";
                                }
                                break;
                        }
                    }
                }

                // Final Adjustment
                forEach(filters.value.facets, function (value, key) {
                    let findings = find(facets.value, function (o) {
                        return o.param == key;
                    });
                    if (isNil(findings)) {
                        delete filters.value.facets[key];
                        callAgain = true;
                    }
                });

                if (callAgain) {
                    filters.value.page = 1;
                }

                resolve(callAgain);
            });
        }

        function getFacet(param) {
            let facet = find(facets.value, {param: param});
            return facet;
        }

        function setFilter(param, value, facet) {
            if (facet) {
                if (facet.multiValue) {
                } else {
                    filters.value.facets[param] = value;
                }
            } else {
                filters.value[param] = value;
            }
            if (param == 'radius') {
                filterChanged(true);
            } else {
                filterChanged();
            }
        }

        function setFilterColor(param, value, facet) {
            if (facet) {
                if (facet.multiValue) {
                } else {
                    if (filters.value.facets[param] == value) {
                        return "primary";
                    }
                }
            } else {
                if (filters.value[param] == value) {
                    return "primary";
                }
            }
        }

        function locationChanged(addressData, placeResultData) {
            filters.value.location = placeResultData.formatted_address;
            filterChanged(true);
        }

        function filterChanged(resetCoordinates = false) {
            updatePath(context.root.$router, filters, resetCoordinates, props.rectBounds, props.center);
            context.emit('filtersReady', resetCoordinates);
        }

        function showFilter(filter) {
            const showFilter = find(props.filterOptions.filters, { 'value': filter.value, 'show': true });
            if(showFilter) {
                if(filter.type == 'facet') {
                    const facet = getFacet(filter.value);
                    return facet;
                }
                return true;
            } else {
                return false;
            }

            return showFilter || facet;

        }

        return {
            facets,
            filters,
            mainFilters,
            max_full_value,
            occupancyChoices,
            maxPriceChoices,
            radiusChoices,
            filteredFacets,
            reRenderFacets,
            getFacet,
            setFilter,
            setFilterColor,
            locationChanged,
            filterChanged,
            togglePanel,
            includes,
            find,
            has,
            showFilter
        }
    },
})
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.widget-search {
    .v-btn-sort{
        height: 56px;
    }

    .sidebar {
        position: relative;
        padding-top: 30px;
        padding-bottom: 30px;
        margin-left: 0px;
        margin-right: 0px;
        background: #F5F5F5;

        &:before, &:after {
            position: absolute;
            content: '';
            width: 1000px;
            top: 0;
            bottom: 0;
            background: inherit;
        }

        &:before {
            right: 100%;
        }

        &:after {
            left: 100%;
        }

        @media #{map-get($display-breakpoints, 'md-and-up')}{
            height: 100%;
            min-height: 100vh;

            &:after {
                display: none;
            }
        }

        @media #{map-get($display-breakpoints, 'md-and-down')}{
            padding-bottom: 0px;

            &-overlay {
                position: absolute;
                top: 0;
                left: 23px;
                right: 23px;
                z-index: 4;

                &:before {
                    width: 200vw;
                    left: -100vw;
                    right: unset;
                    z-index: -1;
                }

                &:after {
                    display: none;
                }

                &-active {
                    &:before {
                        -webkit-box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.2);
                        -moz-box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.2);
                        box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.2);
                    }
                }
            }

            &:not(.overlay) {
                margin: 0px -15px;
            }
        }

        .autocomplete-field {
            .v-input__slot {
                height: 50px;
                border-radius: 0;
            }
        }

        .v-expansion-panel {
            background: transparent !important;

            &-content {
                &__wrap {
                    display: flex;
                    padding: 0px !important;
                }
            }
        }
    }
}
</style>
